import './euras-marriage-documents.scss';

import { useFormContext, useWatch } from 'react-hook-form';

import { Button, DocumentController } from '@/components/Shared/ui';
import { downloadFilesApi } from '@/integrations/index.api';
import { useAppSelector } from '@/store';
import type { TFileAttached } from '@/types/financing';
import type { FileType, TDocumentsOnModeration } from '@/types/uploadFiles';

import { type FormType, fileDict } from '../../features/euras-bank-v2';

interface IProps {
  extrajudicialEnforcement?: TFileAttached;
  marriageContract?: TFileAttached;
  spouseConsent?: TFileAttached;
  filesOnModeration?: Record<string, TFileAttached>;
  handleSubmit: () => Promise<void>;
  documentsOnModeration: TDocumentsOnModeration[];
}

const EurasMarriageDocuments: React.FC<IProps> = ({
  extrajudicialEnforcement,
  marriageContract,
  spouseConsent,
  filesOnModeration,
  handleSubmit,
  documentsOnModeration
}) => {
  const {
    userAccesses: {
      global: { isAdmin }
    }
  } = useAppSelector((state) => state.userInfo);
  const { orderApp } = useAppSelector((state) => state.newOrder);
  const { selectedBank, isEuBankOfflineProcess } = useAppSelector(
    (state) => state.financingStep
  );
  const { control, setValue } = useFormContext<FormType>();

  const [
    extrajudicialEnforcementForm,
    marriageContractForm,
    spouseConsentForm
  ] = useWatch({
    control,
    name: ['extrajudicialEnforcement', 'marriageContract', 'spouseConsent']
  });

  const isPersonMarried = Number(orderApp?.personalInfo.familyStatus) === 1;
  const vehicleRegistration =
    selectedBank?.status.code === 'vehicle_registration';
  const onRework = selectedBank?.status.code === 'on_rework';
  const showResendDocumentsOffline =
    isAdmin &&
    !orderApp?.isOnlineRequest &&
    vehicleRegistration &&
    !!extrajudicialEnforcement &&
    !!marriageContract &&
    !!spouseConsent;
  // Если нет "Государственный номерной знак" или "Номер свидетельства о регистрации ТС"
  // то поля для загрузки документов будут заморожены
  // const isFieldsFrozen =
  //   !tsModalValues.licensePlate || !tsModalValues.registerNum;
  const isReuploadable: boolean =
    !!orderApp?.isOnlineRequest &&
    selectedBank?.status.code === 'waiting_for_docs_confirmation';

  const resendDocumentsOffline = async () => {
    if (
      !vehicleRegistration ||
      !extrajudicialEnforcement ||
      !marriageContract ||
      !spouseConsent
    )
      return;

    const documents = [
      ['extrajudicialEnforcement', extrajudicialEnforcement],
      ['marriageContract', marriageContract],
      ['spouseConsent', spouseConsent]
    ] as const;

    for (const [key, value] of documents) {
      const fileResponse = await downloadFilesApi.downloadBankFile(value.uuid);
      setValue(key, {
        base64: fileResponse.base64,
        name: fileResponse.name,
        size: 0,
        reUpload: true
      });
    }

    await handleSubmit();

    for (const [key] of documents) {
      setValue(key, {
        base64: '',
        name: '',
        size: 0
      });
    }
  };

  // Дублирование документов для разных полей формы
  // Зависит от статуса клиента (женат/не женат)
  const handleMarriageDocumentChange = (
    file: Partial<FileType> | null | undefined,
    name: string
  ) => {
    const marriageDocuments = [
      'extrajudicialEnforcement',
      'marriageContract',
      'spouseConsent'
    ] as const;

    const isOnModeration = (documentType: string): boolean => {
      return documentsOnModeration.some(
        (doc) => doc.documentType === documentType
      );
    };

    if (marriageDocuments.some((key) => isOnModeration(key))) {
      return;
    }

    if (isPersonMarried) {
      if (name === 'extrajudicialEnforcement') {
        setValue('spouseConsent', file as FileType);
      }
      if (name === 'spouseConsent') {
        setValue('extrajudicialEnforcement', file as FileType);
      }
    } else {
      if (name !== 'extrajudicialEnforcement') {
        setValue('extrajudicialEnforcement', file as FileType);
      }
      if (name !== 'marriageContract') {
        setValue('marriageContract', file as FileType);
      }
      if (name !== 'spouseConsent') {
        setValue('spouseConsent', file as FileType);
      }
    }
  };

  return (
    <div
      className={`euras-marriage-documents ${!showResendDocumentsOffline ? 'euras-marriage-documents--no-resend' : ''}`}
    >
      <DocumentController
        control={control}
        name="extrajudicialEnforcement"
        title={fileDict.extrajudicialEnforcement}
        file={extrajudicialEnforcementForm}
        initialFileName={extrajudicialEnforcement?.name}
        fileLink={extrajudicialEnforcement?.uuid || ''}
        afterFileChange={handleMarriageDocumentChange}
        uploadable={!extrajudicialEnforcement || isReuploadable}
        downloadable={extrajudicialEnforcement && !onRework}
        isRequired={!!filesOnModeration?.extrajudicialEnforcement}
      />

      <DocumentController
        control={control}
        name="marriageContract"
        title={fileDict.marriageContract}
        file={marriageContractForm}
        initialFileName={marriageContract?.name}
        fileLink={marriageContract?.uuid || ''}
        afterFileChange={handleMarriageDocumentChange}
        uploadable={
          (!marriageContract &&
            (vehicleRegistration ||
              !!filesOnModeration?.marriageContract ||
              isEuBankOfflineProcess)) ||
          isReuploadable
        }
        downloadable={marriageContract && !onRework}
        isRequired={onRework || !!filesOnModeration?.marriageContract}
      />

      <DocumentController
        control={control}
        name="spouseConsent"
        title={fileDict.spouseConsent}
        file={spouseConsentForm}
        initialFileName={spouseConsent?.name}
        fileLink={spouseConsent?.uuid || ''}
        afterFileChange={handleMarriageDocumentChange}
        uploadable={!spouseConsent || isReuploadable}
        downloadable={spouseConsent && !onRework}
        isRequired={!!filesOnModeration?.spouseConsent}
      />

      {showResendDocumentsOffline && (
        <Button
          type="button"
          variant="outlined-red"
          onClick={resendDocumentsOffline}
          fullWidth
        >
          Переотправить документы
        </Button>
      )}
    </div>
  );
};

export default EurasMarriageDocuments;
